import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissionStore } from 'src/models/usePermissionModel';
import {
  EnableDisableEnum,
  ExchangeOnlinePolicyItem,
  TabsEnum,
} from 'src/pages/Policy/utils/types';
import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import { handleNavigate, withEditPermission } from 'src/utils/common';
import { PermissionNameEnum } from '@Types/permissions';
import { FilterStatusArrType } from '@Policy/Components/PolicyTabs/context';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { PolicyShell } from '@Policy/PolicyShell';
import { PolicySubTypeEnum } from '@Types/index';

export const AtpPolicy = (props: { tabs: TabsEnum[]; tabPanels: any[] }) => {
  const { tabs, tabPanels } = props;
  const navigate = useNavigate();

  const permissionModel = usePermissionStore();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;

  const fbEnabled = policySelector((s) => s.tmFileblockings.fbEnabled);
  const vsEnableVirusscan = policySelector(
    (s) => s.tmVirusscans.vsEnableVirusscan
  );
  const wtpEnabled = policySelector((s) => s.tmWebthreatprotections.wtpEnabled);
  const ddaEnabled = policySelector((s) => s.tmDdafilters.ddaEnabled);
  const asEnabled = policySelector((s) => s.tmAntispamfilters.asEnabled);
  let raiEnabled = 0;
  const policyMode = policySelector((s) => s.policyMode);
  const policyId = policySelector((s) => s.policyUuid);
  const policyType = policySelector((s) => s.policyType);
  const policySubType = policySelector((s) => s.policySubType);
  const activeTabIndex = contextSelector((s) => s.activeTabIndex);
  const setActiveTabIndex = contextSelector((s) => s.setActiveTabIndex);
  const getRecipientGroups = contextSelector((s) => s.getRecipientGroups);
  const submitPolicy = contextSelector((s) => s.submitPolicy);
  const tabStatusMap: Map<TabsEnum, EnableDisableEnum | null> = new Map([
    [TabsEnum.general, null],
    [TabsEnum.advancedSpamProtection, asEnabled],
    [TabsEnum.malwareScanning, vsEnableVirusscan],
    [TabsEnum.fileBlocking, fbEnabled],
    [TabsEnum.webReputation, wtpEnabled],
    [TabsEnum.virtualAnalyzer, ddaEnabled],
  ]);
  if (
    [PolicyTypeEnum.Exchange, PolicyTypeEnum.Gmail].includes(policyType) ||
    ([PolicyTypeEnum.ExchangeInline, PolicyTypeEnum.GmailInline].includes(
      policyType
    ) &&
      policySubType === PolicySubTypeEnum.inbound)
  ) {
    raiEnabled = policySelector((s) => s.tmRiskAiFilters.raiEnabled);
    tabStatusMap.set(TabsEnum.riskAi, raiEnabled);
  }

  const tabStatusArr = useMemo(() => {
    const status: FilterStatusArrType = [];
    tabs.forEach((tab) => {
      if (tabStatusMap.has(tab)) {
        status.push(
          tabStatusMap.get(tab) === null ? null : tabStatusMap.get(tab) ? 1 : 0
        );
      }
    });
    return status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fbEnabled,
    vsEnableVirusscan,
    wtpEnabled,
    ddaEnabled,
    asEnabled,
    raiEnabled,
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const saveDisabled = useMemo(() => {
    return (
      !withEditPermission(
        PermissionNameEnum.atpPolicy,
        permissionModel.permissions
      ) || policyMode === 3
    );
  }, [permissionModel.permissions, policyMode]);

  const handleSubmitPolicy = useCallback(async () => {
    setIsLoading(true);
    await submitPolicy({
      successCallback: () => {
        const category = [1, 21, 22, 82].includes(policyType)
          ? 'email'
          : 'collaboration';
        const atpPolicyListUrl = `/policy/advanced-threat-protection/${category}`;
        handleNavigate(atpPolicyListUrl);
        navigate(atpPolicyListUrl);
      },
      policyId,
    });
    setIsLoading(false);
  }, [submitPolicy, policyId, policyType, navigate]);

  const goBack = useCallback(() => {
    const category = [1, 21, 22, 82].includes(policyType)
      ? 'email'
      : 'collaboration';
    const atpPolicyListUrl = `/policy/advanced-threat-protection/${category}`;
    handleNavigate(atpPolicyListUrl);
    navigate(atpPolicyListUrl);
  }, [navigate, policyType]);

  useEffect(() => {
    getRecipientGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PolicyShell
      tabs={tabs}
      tabPanels={tabPanels}
      tabStatusArr={tabStatusArr}
      activeTabIndex={activeTabIndex}
      setActiveTabIndex={setActiveTabIndex}
      handleSubmitPolicy={handleSubmitPolicy}
      saveDisabled={saveDisabled}
      isLoading={isLoading}
      goBack={goBack}
      policyType={policyType}
    />
  );
};
